import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { formatDate, formatTime } from '@/lib/datetime'
import { formatMoney } from '@/lib/money'
import { cn } from '@/lib/cn'
import { accessToken } from '@/lib/staffSession'

import { useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { SelectField, TextField } from '../components/fields'
import { ReservationDetailPanel } from '../components/ReservationDetailPanel'
import { useAdminContext } from '../session'
import type { ReservationPage, ReservationRow } from '../types'

const PAGE_SIZE = 50

type StatusGroup = 'confirmed' | 'pending' | 'cancelled' | 'all'
type SortKey = 'recent' | 'oldest' | 'name' | 'people' | 'total'

/**
 * Les réservations : qui a réservé quoi, et comment le joindre.
 *
 * Un écran à part plutôt qu'un onglet de « Commandes ». « Commandes » sert à
 * l'argent — encaisser, relancer, rembourser — et s'ouvre sur une commande à
 * la fois. Ici, on lit la salle entière : un acheteur par ligne, son e-mail,
 * son WhatsApp, ce qu'il a réservé et pour combien de personnes. C'est la
 * liste qu'on exporte après la soirée pour dire merci.
 *
 * Sur téléphone, une carte par réservation ; à partir d'un grand écran, un
 * tableau. Le même contenu, sans défilement horizontal.
 */
export function AdminReservationsPage() {
  const { t, i18n } = useTranslation()
  const { eventId, event, can } = useAdminContext()
  const timeZone = event?.timezone ?? 'UTC'

  useEffect(() => {
    document.title = t('admin.section.reservations')
  }, [t])

  const [status, setStatus] = useState<StatusGroup>('confirmed')
  const [consent, setConsent] = useState<'' | 'true'>('')
  const [sort, setSort] = useState<SortKey>('recent')
  const [draft, setDraft] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  // La réservation ouverte. `?open=<commande>` : on arrive ici depuis une
  // demande de table ou une commande, directement sur son détail.
  const [params, setParams] = useSearchParams()
  const openId = params.get('open')
  function open(orderId: string | null) {
    const next = new URLSearchParams(params)
    if (orderId) next.set('open', orderId)
    else next.delete('open')
    setParams(next, { replace: true })
  }

  // On attend que la frappe se pose : une requête par lettre ferait clignoter
  // la liste pendant qu'on écrit un nom.
  useEffect(() => {
    const next = draft.trim()
    if (next === search) return
    const timer = window.setTimeout(() => {
      setSearch(next)
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [draft, search])

  // Un autre filtre, c'est une autre liste : on repart de sa première page.
  // Remis à 1 dans le même geste, pour ne jamais demander « page 3 » d'une
  // sélection qui n'en a qu'une.
  function refilter<T>(set: (value: T) => void) {
    return (value: T) => {
      set(value)
      setPage(1)
    }
  }

  const filters = useMemo(() => {
    const params = new URLSearchParams({ status, sort })
    if (consent) params.set('consent', consent)
    if (search) params.set('q', search)
    return params
  }, [status, consent, sort, search])

  const query = useAdminQuery<ReservationPage>(
    ['admin-reservations', eventId, filters.toString(), page],
    `/admin/events/${eventId}/reservations?${filters.toString()}&page=${page}&page_size=${PAGE_SIZE}`,
    eventId,
  )
  const data = query.data
  const rows = data?.items ?? []
  // Faux pour la porte (`orders.amount.read`) : le serveur a retiré les
  // montants, l'écran retire la colonne — pas de cases vides ni de « 0 € ».
  const amountsVisible = data?.amounts_visible !== false

  // Le fichier suit exactement ce qui est affiché : mêmes filtres, même
  // recherche. Exporter « tout » quand on regarde « ceux qui ont accepté »
  // ferait partir une promotion à des gens qui ne l'ont pas demandée.
  const exporter = useMutation({
    mutationFn: async () => {
      const prefix = import.meta.env.VITE_API_PREFIX ?? '/api/v1'
      const exportParams = new URLSearchParams(filters)
      exportParams.set('sort', 'name')
      const response = await fetch(
        `${prefix}/admin/events/${eventId}/reservations/export?${exportParams.toString()}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await accessToken()}`,
            ...(eventId ? { 'X-Event-Id': eventId } : {}),
          },
          body: JSON.stringify({ reason: null }),
        },
      )
      if (!response.ok) throw new Error(String(response.status))
      const disposition = response.headers.get('Content-Disposition') ?? ''
      const match = /filename="([^"]+)"/.exec(disposition)
      const url = URL.createObjectURL(await response.blob())
      const link = document.createElement('a')
      link.href = url
      link.download = match?.[1] ?? 'reservations.csv'
      link.click()
      URL.revokeObjectURL(url)
    },
  })

  const money = (minor: number | null, currency: string) =>
    minor === null ? '' : formatMoney({ amount_minor: minor, currency }, i18n.language)
  const when = (iso: string) =>
    `${formatDate(iso, i18n.language, timeZone)} · ${formatTime(iso, i18n.language, timeZone)}`

  return (
    <>
      <AdminHeader
        title={t('admin.section.reservations')}
        description={t('admin.reservations.description')}
        action={
          can('orders.order.export') ? (
            <div className="flex flex-col items-start gap-1 sm:items-end">
              <Button
                variant="outline"
                size="sm"
                disabled={exporter.isPending || !data || data.total === 0}
                onClick={() => exporter.mutate()}
              >
                {exporter.isPending
                  ? t('admin.reservations.exporting')
                  : t('admin.reservations.export', { count: data?.total ?? 0 })}
              </Button>
              {exporter.isError ? (
                <span className="text-sm text-state-danger">
                  {t('admin.reservations.exportFailed')}
                </span>
              ) : null}
            </div>
          ) : null
        }
      />

      {/* Le cadre légal, en deux phrases : ce qu'on peut envoyer à tous, ce
          qui demande l'accord. Pas un avertissement de plus qu'on ne lit pas. */}
      <p className="mb-6 rounded-md border border-line-subtle bg-bg-elevated p-4 text-sm text-content-secondary">
        {t('admin.reservations.consentNotice')}
      </p>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <TextField
          label={t('admin.reservations.search')}
          placeholder={t('admin.reservations.searchPlaceholder')}
          value={draft}
          onChange={setDraft}
        />
        <SelectField<StatusGroup>
          label={t('admin.reservations.filterStatus')}
          {...(status === 'confirmed' ? { hint: t('admin.reservations.groupHint') } : {})}
          value={status}
          onChange={refilter(setStatus)}
          options={(['confirmed', 'pending', 'cancelled', 'all'] as const).map((value) => ({
            value,
            label: t(`admin.reservations.group.${value}`),
          }))}
        />
        <SelectField<'' | 'true'>
          label={t('admin.reservations.filterConsent')}
          value={consent}
          onChange={refilter(setConsent)}
          options={[
            { value: '', label: t('admin.reservations.consentAny') },
            { value: 'true', label: t('admin.reservations.consentOnly') },
          ]}
        />
        <SelectField<SortKey>
          label={t('admin.reservations.sort')}
          value={sort}
          onChange={refilter(setSort)}
          options={(['recent', 'oldest', 'name', 'people', 'total'] as const)
            .filter((value) => amountsVisible || value !== 'total')
            .map((value) => ({
              value,
              label: t(`admin.reservations.sortBy.${value}`),
            }))}
        />
      </div>

      {openId ? <ReservationDetailPanel orderId={openId} onClose={() => open(null)} /> : null}

      {data ? (
        <dl className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label={t('admin.reservations.stat.reservations')} value={data.summary.reservations} />
          <Stat label={t('admin.reservations.stat.people')} value={data.summary.people} />
          <Stat label={t('admin.reservations.stat.whatsapp')} value={data.summary.with_whatsapp} />
          <Stat label={t('admin.reservations.stat.consent')} value={data.summary.with_consent} />
        </dl>
      ) : null}

      {query.isError ? (
        <p role="alert" className="text-sm text-state-danger">
          {t('admin.reservations.loadFailed')}
        </p>
      ) : query.isPending ? (
        <p className="text-sm text-content-muted">{t('admin.reservations.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-line-subtle p-6 text-center text-sm text-content-muted">
          {search ? t('admin.reservations.noMatch') : t('admin.reservations.empty')}
        </p>
      ) : (
        <>
          {/* Téléphone et tablette : une carte par réservation. */}
          <ul className="space-y-3 xl:hidden">
            {rows.map((row) => (
              <li
                key={row.order_id}
                className={cn(
                  'min-w-0 rounded-lg border bg-bg-elevated p-4',
                  openId === row.order_id ? 'border-champagne-400/60' : 'border-line-subtle',
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="break-words text-content-primary">{buyer(row, t)}</p>
                    <p className="mt-0.5 text-caption text-content-muted">
                      {row.order_number} · {when(row.created_at)}
                    </p>
                  </div>
                  {amountsVisible ? (
                    <p className="shrink-0 text-right tabular-nums text-content-primary">
                      {money(row.total_minor, row.currency)}
                    </p>
                  ) : null}
                </div>
                <Contact row={row} />
                <p className="mt-3 break-words text-sm text-content-secondary">
                  {row.nature || '—'}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Pill>{t('admin.reservations.peopleCount', { count: row.people })}</Pill>
                  {row.bracelets ? (
                    <Pill>{t('admin.orders.bracelets.badge', { count: row.bracelets })}</Pill>
                  ) : null}
                  <PaymentPill row={row} />
                  {row.marketing_consent ? (
                    <Pill tone="success">{t('admin.reservations.consentYes')}</Pill>
                  ) : null}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  aria-expanded={openId === row.order_id}
                  onClick={() => open(openId === row.order_id ? null : row.order_id)}
                >
                  {openId === row.order_id
                    ? t('admin.reservations.detail.close')
                    : t('admin.reservations.detail.open')}
                </Button>
              </li>
            ))}
          </ul>

          {/* Grand écran : le tableau, sans défilement horizontal. */}
          <div className="hidden rounded-lg border border-line-subtle xl:block">
            <table className="w-full table-fixed text-sm">
              <thead>
                <tr className="border-b border-line-subtle bg-bg-elevated text-left">
                  <Th className="w-[18%]">{t('admin.reservations.col.buyer')}</Th>
                  <Th className="w-[22%]">{t('admin.reservations.col.contact')}</Th>
                  <Th>{t('admin.reservations.col.nature')}</Th>
                  <Th className="w-[6%] text-right">{t('admin.reservations.col.people')}</Th>
                  {amountsVisible ? (
                    <Th className="w-[10%] text-right">{t('admin.reservations.col.total')}</Th>
                  ) : null}
                  <Th className={amountsVisible ? 'w-[12%]' : 'w-[18%]'}>
                    {t('admin.reservations.col.payment')}
                  </Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.order_id}
                    className={cn(
                      'border-b border-line-subtle/50 align-top',
                      openId === row.order_id && 'bg-bg-overlay/40',
                    )}
                  >
                    <td className="px-3 py-2.5">
                      <button
                        type="button"
                        aria-expanded={openId === row.order_id}
                        onClick={() => open(openId === row.order_id ? null : row.order_id)}
                        className="block break-words text-left text-champagne-300 underline-offset-2 hover:underline"
                      >
                        {buyer(row, t)}
                      </button>
                      <span className="block text-caption text-content-muted">
                        {row.order_number}
                      </span>
                      <span className="block text-caption text-content-muted">
                        {when(row.created_at)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <Contact row={row} compact />
                    </td>
                    <td className="break-words px-3 py-2.5 text-content-secondary">
                      {row.nature || '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{row.people}</td>
                    {amountsVisible ? (
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {money(row.total_minor, row.currency)}
                      </td>
                    ) : null}
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1.5">
                        <PaymentPill row={row} />
                        {row.bracelets ? (
                          <Pill>{t('admin.orders.bracelets.badge', { count: row.bracelets })}</Pill>
                        ) : null}
                        {row.marketing_consent ? (
                          <Pill tone="success">{t('admin.reservations.consentYes')}</Pill>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data && data.pages > 1 ? (
            <nav className="mt-6 flex items-center justify-between gap-3">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(current - 1, 1))}
              >
                {t('admin.reservations.previous')}
              </Button>
              <span className="text-sm text-content-muted">
                {t('admin.reservations.pageOf', { page, pages: data.pages })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= data.pages}
                onClick={() => setPage((current) => Math.min(current + 1, data.pages))}
              >
                {t('admin.reservations.next')}
              </Button>
            </nav>
          ) : null}
        </>
      )}
    </>
  )
}

function buyer(row: ReservationRow, t: (key: string) => string): string {
  if (row.anonymized) return t('admin.reservations.anonymized')
  const name = [row.first_name, row.last_name].filter(Boolean).join(' ').trim()
  return name || t('admin.reservations.noName')
}

/** L'e-mail et le WhatsApp, cliquables : écrire, c'est la raison de cet écran. */
function Contact({ row, compact = false }: { row: ReservationRow; compact?: boolean }) {
  const { t } = useTranslation()
  if (!row.email && !row.whatsapp) {
    return (
      <p className={cn('text-sm text-content-muted', !compact && 'mt-2')}>
        {t('admin.reservations.noContact')}
      </p>
    )
  }
  return (
    <div className={cn('min-w-0 space-y-0.5 text-sm', !compact && 'mt-2')}>
      {row.email ? (
        <a
          href={`mailto:${row.email}`}
          className="block break-all text-champagne-300 underline-offset-2 hover:underline"
        >
          {row.email}
        </a>
      ) : null}
      {row.whatsapp ? (
        <a
          href={`https://wa.me/${row.whatsapp.replace(/\D/g, '')}`}
          target="_blank"
          rel="noreferrer"
          className="block break-all text-content-secondary underline-offset-2 hover:underline"
        >
          WhatsApp · {row.whatsapp}
        </a>
      ) : null}
    </div>
  )
}

function PaymentPill({ row }: { row: ReservationRow }) {
  const { t, i18n } = useTranslation()
  if (row.payment === 'closed') {
    return <Pill tone="muted">{t(`admin.orders.status.${row.status}`)}</Pill>
  }
  // Réglée à l'entrée, au scan : le dire plutôt qu'un simple « Payé ».
  if (row.payment === 'paid' && row.settled_at_door) {
    return <Pill tone="success">{t('admin.orders.atDoor.settledBadge')}</Pill>
  }
  // À régler à l'entrée : avec le montant pour qui le voit, sans pour la porte
  // (elle le lira au scan, au moment d'encaisser).
  if (row.payment === 'at_door') {
    const due = row.amount_due_minor
    return (
      <Pill tone="warning">
        {due === null || due === undefined
          ? t('admin.orders.atDoor.due')
          : t('admin.orders.atDoor.dueAmount', {
              amount: formatMoney({ amount_minor: due, currency: row.currency }, i18n.language),
            })}
      </Pill>
    )
  }
  return (
    <Pill tone={row.payment === 'paid' ? 'success' : row.payment === 'unpaid' ? 'warning' : 'neutral'}>
      {t(`admin.reservations.payment.${row.payment}`)}
    </Pill>
  )
}

function Pill({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'success' | 'warning' | 'muted'
  children: React.ReactNode
}) {
  const tones = {
    neutral: 'border-line text-content-secondary',
    success: 'border-state-success/40 text-state-success',
    warning: 'border-ember-400/40 text-ember-300',
    muted: 'border-line-subtle text-content-muted',
  } as const
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-caption',
        tones[tone],
      )}
    >
      {children}
    </span>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-line-subtle bg-bg-elevated p-3">
      <dt className="text-caption text-content-muted">{label}</dt>
      <dd className="mt-1 font-display text-heading-sm tabular-nums text-content-primary">
        {value}
      </dd>
    </div>
  )
}

function Th({ className, children }: { className?: string; children: React.ReactNode }) {
  return <th className={cn('px-3 py-2 font-normal text-content-muted', className)}>{children}</th>
}
